import React from 'react';
import { createPortal } from 'react-dom';
import { Col, Container, Row, Button, OverlayTrigger, Popover } from 'react-bootstrap';
import ChemComponent from '../ChemComponent';
import ChemEdit from '../ChemEdit';
import ChemTable from '../ChemTable';
import EditTable from '../EditTable';
import Tabs from '../Tabs';
import { Link } from 'react-router-dom';
import { withProjectDefaultRoute } from './withProjectDefaultRoute';
import { LockFill, UnlockFill, HourglassSplit, EnvelopeAt, XSquare } from 'react-bootstrap-icons';
import EmailModal from './EmailModal';
import { encode } from 'html-entities';
import HashLink from '../HashLink';

class Project extends ChemComponent {
	constructor(props) {
		super(props);
		
		this.state = {
			project: {
				shop_project: {
					PROJECT_ID: ''
				},
				estimate: [],
				estimate_total: {},
				add_job: {},
				job: [],
				job_total: {},
				project_scope: {},
				project_doc: [],
				project_comment: [],
				summary: {},
				sum_totals: {}
			},
			piColumns: this.copyColumns(piColumns),
			addJobColumns: this.copyColumns(addJobColumns),
			showEmail: false,
			activeTab: props.params.activeTab || 'Description',
			editorsOpen: [],
			editable: props.editable,
			dirty: false,
			renderKey: 0
		}
	}	

	componentDidMount() {
		this.loadProject(this.props.params.activeTab || 'Description');
	}

	componentDidUpdate(prevProps) {
		var newActiveTab = this.props.params.activeTab || 'Description';
		
		// if this is a different job, reload the job
		if (this.props.params.projectId !== prevProps.params.projectId) {
			this.loadProject(newActiveTab);
		} else {
			var newState = this.copyObject(this.state);
			var newRenderKey = this.state.renderKey + 1;
						
			// this is necessary so browser back/forward buttons will work across tabs
			if (newActiveTab !== this.state.activeTab) {
				newState.activeTab = newActiveTab;
				newState.renderKey = newRenderKey;
			}

			// if anyone actually updated the state
			if (newState.renderKey > this.state.renderKey) {
				// set the new state
				this.setState(newState);
			}
		}
	}

	loadProject(activeTab) {
		var self = this;

		this.ajax({
			type: 'post',
			url: this.getConfig().host + '/Jobtrack/GetProject',
			data: { 
				__RequestVerificationToken: this.props.user.antiForgeryToken,
				projectid: this.props.params.projectId
			}
		}).done(function (data) {			
			if (data.Success) {
				// prepare job data
				var i;
				var project = data.Data;
				
				// compose change info
				project.shop_project.CREATED = self.composeChangeInfo(project.shop_project.ENTERED_BY, project.shop_project.ENTERED);
				project.shop_project.UPDATED = self.composeChangeInfo(project.shop_project.UPDATED_BY, project.shop_project.LAST_UPDATE);
				
				// calculate project estimate - this causes infinite recursion
				// because of something in ChemEdit
				self.calculateEstimate(project);
				
				// configure PI columns
				var newPIColumns = self.copyColumns(piColumns);
				if (!self.isEmpty(project.shop_project.PROJECT_PI)) {
					var piIdx = self.findColumnByAccessor(newPIColumns, 'shop_project.PROJECT_PI');
					newPIColumns[piIdx].options.search.Children.push(
						{ Attribute: 'ROLE_ID', Operator: '=', LongValue: project.shop_project.PROJECT_PI });
				}
				if (!self.isEmpty(project.shop_project.CONTACT_ID)) {
					var custIdx = self.findColumnByAccessor(newPIColumns, 'shop_project.CONTACT_ID');
					newPIColumns[custIdx].options.search.Children.push(
						{ Attribute: 'ROLE_ID', Operator: '=', LongValue: project.shop_project.CONTACT_ID });
				}
				
				// configure addJob Columns
				var newAddJobColumns = self.copyColumns(addJobColumns);
				var jobIdIdx = self.findColumnByAccessor(newAddJobColumns, 'add_job.JOB_ID');
				// if there are jobs on the project
				if (project.job.length > 0) {
					// exclude all jobs that are already on the project
					newAddJobColumns[jobIdIdx].options.search = { Attribute: 'JOB_ID', Operator: 'not in', LongList: [] };
					for (i = 0; i < project.job.length; i++) {
						newAddJobColumns[jobIdIdx].options.search.LongList.push(project.job[i].JOB_ID);
					}
				}
				
				// copy state
				var newState = self.copyObject(self.state);

				// add new data we just loaded
				newState.project = project;
				newState.project.add_job = {};
				newState.dirty = false;
				newState.activeTab = activeTab;
				newState.piColumns = newPIColumns;
				newState.addJobColumns = newAddJobColumns;
				
				// update render key
				newState.renderKey = self.state.renderKey + 1;
				
				// set state
				self.setState(newState);
			} else {
				self.showAlert('Server Error', data.Message);
			}			
		}).fail(function (jqXHR, textStatus, errorThrown) {
			self.showAlert('Server Error', 'Server returned a status of ' + jqXHR.status);
		});
	}
		
	canClickTabItem(tab) {
		// prevent user from leaving a tab if an editor is open
		if (this.state.activeTab === 'Scope_Docs' && this.state.editorsOpen.includes('project_doc')) {
			this.showAlert('Editor Open', 'Please close the file editor before leaving the Scope/Docs tab.');
			return false;
		} else if (this.state.activeTab === 'Status' && this.state.editorsOpen.includes('project_comment')) {
			this.showAlert('Editor Open', 'Please close the status editor before leaving the Status tab.');
			return false;
		}
			
		return true;
	}
	
	onClickTabItem(tab) {
		if (tab === 'Scope/Docs') tab = 'Scope_Docs'
		this.props.navigate('/Jobtrack/Projects/' + (this.props.params.projectId || 'new') + '/' + tab);
	}

	onEdit(accessor, editorIsOpen, idx) {
		// adjust list of open editors in new state
		var newState = {
			editorsOpen: this.setEditorStatus(accessor, editorIsOpen),
			renderKey: this.state.renderKey + 1
		};

		this.mergeState(newState);
	}
	
	setEditorStatus(accessor, editorIsOpen) {
		var editorsOpen = this.copyObject(this.state.editorsOpen);
		if (editorIsOpen) {
			// add to list if it isn't already present
			if (!editorsOpen.includes(accessor)) editorsOpen.push(accessor);
		} else {			
			// remove from list if it is present
			if (editorsOpen.includes(accessor)) editorsOpen.splice(editorsOpen.indexOf(accessor), 1);
		}
		return editorsOpen;
	}		

	onChange(accessor, value) {
		var project = this.copyObject(this.state.project);
		this.setByAccessor(project, accessor, value);
		if (['shop_project.ALL_COLLEGE_HRS','shop_project.MATERIAL_COST'].includes(accessor)) {
			this.calculateEstimate(project);
		}
		var newState = {
			project: project,
			dirty: true,
			renderKey: this.state.renderKey + 1
		};
		// if this accessor represents an EditTable
		if (['project_doc','project_comment'].includes(accessor)) {
			// update editor status as well
			newState.editorsOpen = this.setEditorStatus(accessor, false);
		}
		this.mergeState(newState);
	}
	
	calculateEstimate(project) {
		// multiply labor hours by labor rate to get labor cost
		project.shop_project.LABOR_COST = 
			this.round(this.getNumeric(project.shop_project.ALL_COLLEGE_HRS, 0) * 122.0, 2);

		// multiply material cost by markup to get material cost markup
		project.shop_project.MATL_COST_MARKUP =
			this.round(this.getNumeric(project.shop_project.MATERIAL_COST, 0) * 0.2, 2);;
		
		// labor plus material plus markup is Subtotal
		project.shop_project.SUBTOTAL = this.round(
			this.getNumeric(project.shop_project.LABOR_COST, 0) +
			this.getNumeric(project.shop_project.MATERIAL_COST, 0) + 
			this.getNumeric(project.shop_project.MATL_COST_MARKUP, 0), 2);
			
		// contingency is 35% of SUBTOTAL
		project.shop_project.CONTINGENCY = 
			this.round(this.getNumeric(project.shop_project.SUBTOTAL, 0) * 0.35, 2);
		
		// estimate is subtotal plus Contingency
		project.shop_project.ESTIMATE = this.round(
			this.getNumeric(project.shop_project.SUBTOTAL) +
			this.getNumeric(project.shop_project.CONTINGENCY), 2);
	}
	
	addJob(event) {
		var self = this;
		if (this.isEmpty(this.state.project.add_job.JOB_ID)) {
			this.showAlert('No Job Selected', 'Please select a job to add.');
		} else {
			// if the user wants to add a new job
			// (currently not implemented and does not appear in select list)
			if (this.state.project.add_job.JOB_ID === -1) {
				// if this is a new project and/or has been edited 
				if (!this.props.params.projectId || this.state.dirty) {
					// prompt user to save project
					this.showOKCancel('Project Not Saved', 
						'Project must be saved before creating a new job from within the project. Save this project now?',
						(okClicked) => {
							// if user chose to save project
							if (okClicked) {
								// save project
								self.onSubmit((data) => {
									// send resulting project ID to Jobtrack router
									self.props.linkJobProject(null, this.props.params.projectId || data.ID);
									// navigate to new job editor
									self.props.navigate('/Jobtrack/Jobs/new');
								});
							}
						}
					);
				} else {
					// project is already saved so we can go directly to new job editor
					self.props.linkJobProject(null, this.props.params.projectId);
					self.props.navigate('/Jobtrack/Jobs/new');
				}
			} else {
				// look up job info
				this.ajax({
					type: 'post',
					url: this.getConfig().host + '/Jobtrack/Search',
					data: { 
						__RequestVerificationToken: this.props.user.antiForgeryToken,
						entity: 'PROJECT_ADD_JOB_VW',
						search: { Attribute: 'JOB_ID', Operator: '=', LongValue: this.state.project.add_job.JOB_ID },
						pageNumber: -1,
						pageSize: -1
					}
				}).done(function (data) {			
					if (data.Success) {
						if (data.Data.length !== 1) {
							self.showAlert('Job Not Found', 'Expected one job for job ID ' + self.state.project.add_job.JOB_ID +
								' but found ' + data.Data.length);
						} else {
							// make copy of job table
							var newJobs = self.copyObject(self.state.project.job);
							// find max ID of existing rows
							var maxId = -1;
							for (var i = 0; i < newJobs.length; i++) {
								if (newJobs[i].id > maxId) maxId = newJobs[i].id;
							}
							// give new row an id at the bottom of the table
							data.Data[0].id = maxId + 1;
							// add new job to job table
							newJobs.push(data.Data[0]);
							// merge into new state
							self.mergeState({ 
								project: { 
									job: newJobs
								},
								dirty: true
							});
						}
					} else {
						self.showAlert('Server Error', data.Message);
					}			
				}).fail(function (jqXHR, textStatus, errorThrown) {
					self.showAlert('Server Error', 'Server returned a status of ' + jqXHR.status);
				});
			}
		}
	}
	
	openJob(jobId, editable) {
		var self = this;
		if (this.state.dirty) {
			this.showOKCancel('Unsaved Changes', 'Unsaved changes will be lost. Continue?', (okClicked) => {
				if (okClicked) {
					self.props.setJobEditable(editable);
					self.props.navigate('/Jobtrack/Jobs/' + jobId);
				}
			});
		} else {
			this.props.setJobEditable(editable);
			this.props.navigate('/Jobtrack/Jobs/' + jobId);
		}
	}
	
	removeJob(jobId) {
		var self = this;
		
		// locate job
		var job = null;
		for (var i = 0; i < this.state.project.job.length; i++) {
			if (this.state.project.job[i].JOB_ID === jobId) {
				job = this.state.project.job[i];
				break;
			}
		}
		
		// complain if not found
		if (!job) {
			this.showAlert('Job ' + jobId + ' not found.');
		} else {
			// confirm with user
			this.showOKCancel('Remove Job', 'Remove job ' + jobId + (this.isEmpty(job.JOB_REF) ? '' : (': ' + job.JOB_REF)) + '?',
				(okClicked) => {
					if (okClicked) {
						var newJobs = self.copyObject(self.state.project.job);
						newJobs.splice(i, 1);
						self.mergeState({
							project: {
								job: newJobs
							},
							dirty: true
						});
					}
				}
			);
		}		
	}

	onClickEmail(materialId) {
		this.mergeState({ 
			showEmail: true,
			renderKey: this.state.renderKey + 1
		});
	}
	
	sendEmail(email) {
		var self = this;
		
		if (email) {
			this.ajax({
				type: 'post',
				url: this.getConfig().host + '/Jobtrack/SendStatusUpdate',
				data: { 
					__RequestVerificationToken: this.props.user.antiForgeryToken,
					email: email
				}
			}).done(function (data) {			
				if (data.Success) {
					self.setState({ showEmail: false, renderKey: self.state.renderKey + 1 });
				} else {
					self.showAlert('Server Error', data.Message);
				}			
			}).fail(function (jqXHR, textStatus, errorThrown) {
				self.showAlert('Server Error', 'Server returned a status of ' + jqXHR.status);
			});
		} else {
			this.setState({ showEmail: false, renderKey: this.state.renderKey + 1 });
		}
	}

	onSubmit(callback) {
		var self = this;
		var i;
		
		// prevent user from saving if an editor is open
		if (this.state.activeTab === 'Scope_Docs' && this.state.editorsOpen.includes('project_doc')) {
			this.showAlert('Editor Open', 'Please close the file editor before saving.');
		} else if (this.state.activeTab === 'Status' && this.state.editorsOpen.includes('project_comment')) {
			this.showAlert('Editor Open', 'Please close the status editor before saving.');
		} else {
			var project = this.copyObject(this.state.project);
			
			// encode scope and notes so server will accept them
			project.project_scope.PROJECT_SCOPE = encode(project.project_scope.PROJECT_SCOPE);
			project.project_scope.PROJECT_NOTES = encode(project.project_scope.PROJECT_NOTES);

			// for each document
			for (i = 0; i < project.project_doc.length; i++) {
				// if there is a create date
				if (!this.isEmpty(project.project_doc[i].DATE_CREATED)) {
					// make sure it is formatted to send back
					project.project_doc[i].DATE_CREATED = this.dateTimeToMVC(this.getDate(project.project_doc[i].DATE_CREATED));
				}
			}

			// encode project comments so server will accept them
			// also translate Y/N to 2/1
			for (i = 0; i < project.project_comment.length; i++) {
				project.project_comment[i].COMMENT_TEXT = encode(project.project_comment[i].COMMENT_TEXT);
				project.project_comment[i].COMMENT_STATUS = project.project_comment[i].COMMENT_STATUS === 'Y' ? 2 : 1;
			}

			// Create an object of formData
			const formData = new FormData();

			// add the token to the formData
			formData.append('__RequestVerificationToken', this.props.user.antiForgeryToken);
			
			// for each file uploaded to the document table
			var newFileIdx = 0;
			var formFileIdx = 0;
			for (i = 0; i < this.state.project.project_doc.length; i++) {
				// if there is a file
				if (this.state.project.project_doc[i].PROJECT_FILE && this.state.project.project_doc[i].PROJECT_FILE.name) {
					// if this is a new document record
					if (!this.isNumeric(this.state.project.project_doc[i].DOCU_ID)) {
						// make up a unique ID
						project.project_doc[i].DOCU_ID = 'new_' + newFileIdx++;
					}
					
					// add the file information
					formData.append('files[' + (formFileIdx++) + ']', this.state.project.project_doc[i].PROJECT_FILE, project.project_doc[i].DOCU_ID);
				}
			}			

			// add the project to the formData
			var nvpairs = this.createHiddenFormData({ project: project });
			for (i = 0; i < nvpairs.length; i++) {
				formData.append(nvpairs[i].name, nvpairs[i].value);
			}

			this.ajax({
				type: 'POST',
				url: this.getConfig().host + '/Jobtrack/UpdateProject',
				data: formData,
				contentType: false,
				processData: false,
				overlay: true,
			}).done(function (data) {
				if (data.Success) {
					if (callback) {
						callback(data);
					} else {
						self.props.parent.showConfirmation(() => {
							var projectId = self.props.params.projectId || data.ID;
							var verb = self.props.params.projectId ? "updated" : "created";
							return (<>
								Project <Link to={'/Jobtrack/Projects/' + projectId}>{projectId}</Link> {verb}.
							</>);
						});
					}						
				} else {
					self.showAlert('Server Error', data.Message);
				}			
			}).fail(function (jqXHR, textStatus, errorThrown) {
				self.showAlert('Server Error', 'Server returned a status of ' + jqXHR.status);
			});
		}
	}
	
	renderDocDownload(props) {
		if (this.isNumeric(props.value) && this.isEmpty(props.row.values.PROJECT_FILE)) {
			return(
				<OverlayTrigger trigger="hover" overlay={<Popover><Popover.Header as="h3">Download</Popover.Header></Popover>}>
					<form action={this.getConfig().host + '/Jobtrack/DownloadProjectDoc'} method='POST'>
						<input type='hidden' name='__RequestVerificationToken' value={this.props.user.antiForgeryToken} />
						<input type='hidden' name='docid' value={props.value} />
						<input type='image' src={this.getConfig().host + '/Content/Icons/download.png'} alt="Download" />
					</form>
				</OverlayTrigger>
			);
		} else {
			return (
				<OverlayTrigger trigger="hover" overlay={<Popover><Popover.Header as="h3">Pending save</Popover.Header></Popover>}>
					<HourglassSplit />
				</OverlayTrigger>
			);
		}
	}
	
	renderDocImage(imgData) {
		if (!this.isEmpty(imgData)) {
			var style = {
				maxWidth: '300px',
				maxHeight: '300px',
			};
			var alt = 'job image';
			return (<img style={style} src={imgData} alt={alt} />);
		}
	}

	renderEmailModal() {
		var commaIdx = this.isEmpty(this.state.project.shop_project.CUSTOMER_NAME) ? -1 :
			this.state.project.shop_project.CUSTOMER_NAME.indexOf(',');
		var custFirstName = commaIdx === -1 ? this.state.project.shop_project.CUSTOMER_NAME :
			this.state.project.shop_project.CUSTOMER_NAME.substring(commaIdx + 1).trim();
		var comment = this.state.project.project_comment.length === 0 ? this.copyObject({ COMMENT_TEXT: '' }) : 
			this.copyObject(this.state.project.project_comment[this.state.project.project_comment.length - 1]);
		
		var email = {
			TO: this.state.project.shop_project.CUSTOMER_EMAIL,
			REPLY_TO: this.props.user.email,
			CC: this.props.user.email,
			SUBJECT: 'Project ' + this.state.project.shop_project.PROJECT_ID + ' status',
			REFERENCE: this.state.project.shop_project.PROJECT_NAME,
			SALUTATION: this.isEmpty(custFirstName) ? 'Dear Customer' : ('Dear ' + custFirstName),
			BODY: comment.COMMENT_TEXT,
			ENTERED: this.composeChangeInfo(comment.CREATED_BY, comment.DATE_CREATED),
			MODIFIED: this.composeChangeInfo(comment.MODIF_BY, comment.DATE_MODIF),
			SIGNATURE: this.props.user.name
		};

		return (
			<EmailModal parent={this} user={this.props.user} email={email} onClose={(email) => this.sendEmail(email)} />
		);
	}

	render() {	
	
		// make copy of job columns
		var jobColCopy = this.copyColumns(jobColumns);
		// if not editable
		if (!this.state.editable) {
			// find id column
			var idCol = this.findColumnByAccessor(jobColCopy, 'id');
			//remove id column
			jobColCopy.splice(idCol, 1);
		}
	
		return (<>
			<Container fluid>
			  <Row>
			    <Col style={{ paddingTop: '20px', paddingBottom: '10px', border: '1px solid #ccc', backgroundColor: '#FEFEFE' }}>
				  <Container fluid>
					<Row>
					  <Col>
						<div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
						  Project {this.props.params.projectId || '(new)'}&nbsp;&nbsp;
							{this.state.editable ? 
							  <UnlockFill style={{ cursor: 'pointer', marginTop: '-4px' }}
								onClick={() => this.mergeState({ editable: false })} /> : 
							  <LockFill style={{ cursor: 'pointer', marginTop: '-4px' }} 
								onClick={() => this.mergeState({ editable: true })} />}
						</div>
					  </Col>
					</Row>
					<Row>
				      <Tabs activeTab={this.state.activeTab.replace('_', '/')} canClickTabItem={(tab) => this.canClickTabItem(tab)}
						  onClickTabItem={(tab) => this.onClickTabItem(tab)}>
						<div label='Description'>
						  <Container fluid>
							<Row>
						      <Col style={{ paddingBottom: '10px' }}>
								<ChemEdit parent={this} user={this.props.user} columns={this.state.piColumns} data={this.state.project}
								  onChange={(accessor, value) => this.onChange(accessor, value)} renderKey={this.state.renderKey}
								  labelColWidth={6} valueColWidth={6} editable={this.state.editable} />
							  </Col>
							</Row>
							<Row>
							  <Col>
								<ChemEdit parent={this} user={this.props.user} columns={descColumns} data={this.state.project}
								  onChange={(accessor, value) => this.onChange(accessor, value)} renderKey={this.state.renderKey}
								  labelColWidth={3} valueColWidth={9} editable={this.state.editable} />
							  </Col>
							</Row>
						  </Container>
						</div>
						<div label='Estimate'>
						  <Container fluid>
							<Row>
							  <Col>
								<ChemTable parent={this} user={this.props.user} renderKey={this.state.renderKey}
								  data={this.state.project.estimate} editable={false} columns={estimateColumns} 
								  tableName='Jobtrack_Project_Estimate' />
							  </Col>
							</Row>
							<Row>
							  <Col>
								<ChemEdit parent={this} user={this.props.user} columns={estimateTotalColumns} data={this.state.project}
								  onChange={(accessor, value) => this.onChange(accessor, value)} renderKey={this.state.renderKey}
								  editable={false} />
							  </Col>
							</Row>
						  </Container>
						</div>
						<div label='Jobs'>
						  <Container fluid>
							<Row>
							  <Col xs={11}>
								<ChemEdit parent={this} user={this.props.user} columns={this.state.addJobColumns} data={this.state.project}
								  onChange={(accessor, value) => this.onChange(accessor, value)} renderKey={this.state.renderKey}
								  editable={this.state.editable} />
							  </Col>
							  <Col xs={1}>
								{this.state.editable && <Button id="add_job" variant="warning" type="button" className="float-end"
									onClick={() => this.addJob()}>Add</Button>}
							  </Col>
							</Row>
							<Row>
							  <Col>
								<ChemTable parent={this} user={this.props.user} renderKey={this.state.renderKey}
								  data={this.state.project.job} columns={jobColCopy} tableName='Jobtrack_Project_Job' />
							  </Col>
							</Row>
							<Row>
							  <Col>
								<ChemEdit parent={this} user={this.props.user} columns={jobTotalColumns} data={this.state.project}
								  onChange={(accessor, value) => this.onChange(accessor, value)} renderKey={this.state.renderKey}
								  editable={false} />
							  </Col>
							</Row>
						  </Container>
						</div>
						<div label='Scope/Docs'>
						  <Container fluid>
							<Row>
							  <Col>
								<ChemEdit parent={this} user={this.props.user} columns={scopeColumns} data={this.state.project}
								  onChange={(accessor, value) => this.onChange(accessor, value)} renderKey={this.state.renderKey}
								  editable={this.state.editable} labelColWidth={2} valueColWidth={10} />
							  </Col>
							</Row>
							<Row>
							  <Col>
								<EditTable parent={this} user={this.props.user} renderKey={this.state.renderKey} newRecord={emptyDoc}
								  data={this.state.project.project_doc} editable={this.state.editable} tableColumns={docTableColumns} 
								  editColumns={docEditColumns} tableName='Jobtrack_Project_Doc' 
								  onEdit={(editorIsOpen, idx) => this.onEdit('project_doc', editorIsOpen, idx)}
								  onChange={(data, callback) => this.onChange('project_doc', data)} />
							  </Col>
							</Row>
						  </Container>
						</div>
						<div label='Status'>
						  <Container fluid>
						    <Row style={{ marginBottom: '8px' }}>
							  <Col sm={11}>
								<div class='float-end'>Send project status to customer</div>
							  </Col>
							  <Col sm={1}>
								<Button style={{ marginTop: '-8px' }} id="partSearch" variant="warning" type="button"
								  onClick={() => this.mergeState({ showEmail: true })}><EnvelopeAt />
								</Button>
							  </Col>
							</Row>
							<Row>
							  <Col>
								<EditTable parent={this} user={this.props.user} renderKey={this.state.renderKey} newRecord={emptyComment}
								  data={this.state.project.project_comment} editable={this.state.editable} tableColumns={commentTableColumns} 
								  editColumns={commentEditColumns} tableName='Jobtrack_Project_Comment' 
								  onEdit={(editorIsOpen, idx) => this.onEdit('project_comment', editorIsOpen, idx)}
								  onChange={(data, callback) => this.onChange('project_comment', data)}
								  labelColWidth={2} valueColWidth={10} />
							    {this.state.showEmail && createPortal(this.renderEmailModal(), document.body)}
							  </Col>
							</Row>
						  </Container>
						</div>
						<div label='Summary'>
						  <Container fluid>
						    <Row>
							  <Col>
								<ChemEdit parent={this} user={this.props.user} columns={summaryColumns} data={this.state.project}
								  onChange={(accessor, value) => this.onChange(accessor, value)} renderKey={this.state.renderKey}
								  editable={false} />
							  </Col>
							</Row>
							<Row>
							  <Col>
							  	<ChemTable parent={this} user={this.props.user} renderKey={this.state.renderKey}
								  data={this.state.project.job} editable={false} columns={jobColumns} 
								  tableName='Jobtrack_Project_Job_Summary' />
							  </Col>
							</Row>
						  </Container>
						</div>
					  </Tabs>
					</Row>
					<Row>
					  <Col style={{ borderTop: '1px solid #eee', paddingTop: '5px' }}>
						{this.state.editable && <Button id="save" variant="warning" type="button" className="float-end"
							onClick={() => this.onSubmit()}>Save</Button>}
					  </Col>
					</Row>
				  </Container>
				</Col>
			  </Row>
			</Container>
		</>);
	}
}

const piColumns = [
	{ Header: 'PI', accessor: 'shop_project.PROJECT_PI', type: 'select', row: 0, col: 0,
		options: { value: 'ROLE_ID', label: 'PI_NAME', entity: 'RECHARGES.PROJ_PI_EDIT_VW', order: 'PI_NAME',
			search: { Operator: 'or', Children: [{ Attribute: 'ACTIVE_FLAG', Operator: '=', StringValue: 'Y' }]}}},
	{ Header: 'SpeedTypes', accessor: 'shop_project.SPEEDTYPES', row: 0, col: 1, fragment: true, editable: false },
	{ Header: 'Customer', accessor: 'shop_project.CONTACT_ID', labelAccessor: ['shop_project.CUSTOMER_NAME','shop_project.CUSTOMER_EMAIL'], type: 'multicolumn', row: 1, col: 0,
		options: { value: 'ROLE_ID', label: ['CUSTOMER_NAME','CUSTOMER_EMAIL'], entity: 'RECHARGES.PROJ_CUST_EDIT_VW', order: 'CUSTOMER_NAME',
			search: { Operator: 'or', Children: [{ Attribute: 'ACTIVE_FLAG', Operator: '=', StringValue: 'Y' }]}}},
	{ Header: 'Status', accessor: 'shop_project.STATUS_ID', type: 'select', row: 1, col: 1, required: true,
		options: { value: 'STATUS_ID', label: 'STATUS_NAME', entity: 'RECHARGES.JOB_STATUS', order: 'STATUS_NAME',
			search: { Attribute: 'DELETE_FLAG', Operator: 'is null' }}},
	{ Header: 'Start Date', accessor: 'shop_project.PROJECT_START', type: 'date', row: 2, col: 0, required: true },
	{ Header: 'End Date', accessor: 'shop_project.PROJECT_END', type: 'date', row: 2, col: 1 }
];

const descColumns = [
	{ Header: 'Project Name', accessor: 'shop_project.PROJECT_NAME', required: true },
	{ Header: 'Total Approved Budget', accessor: 'shop_project.TOTAL_APPROVED_BUDGET' },		
	{ Header: 'All College Hrs', accessor: 'shop_project.ALL_COLLEGE_HRS' },
	{ Header: 'Material Cost', accessor: 'shop_project.MATERIAL_COST' },
	{ Header: 'Labor Cost ($122/hr)', accessor: 'shop_project.LABOR_COST', editable: false	},
	{ Header: 'Material Cost Mark-up (20%)', accessor: 'shop_project.MATL_COST_MARKUP', editable: false },	
	{ Header: 'Subtotal', accessor: 'shop_project.SUBTOTAL', editable: false },
	{ Header: 'Contingency (35%)', accessor: 'shop_project.CONTINGENCY', editable: false }, 	
	{ Header: 'Estimated Project Cost', accessor: 'shop_project.ESTIMATE', editable: false },
	{ Header: 'Estimate Valid Until', accessor: 'shop_project.ESTIMATE_VALID_UNTIL', type: 'date' },
	{ Header: 'Comments', accessor: 'shop_project.COMMENTS', type: 'textarea' },
	{ Header: 'Created', accessor: 'shop_project.CREATED', editable: false },
	{ Header: 'Updated', accessor: 'shop_project.UPDATED', editable: false }
];

const estimateColumns = [
	{ Header: 'Job', accessor: 'JOB_ID', width: 70,
		Cell: props => <HashLink onClick={() => props.parent.openJob(props.value, false)}>{props.value}</HashLink> },
	{ Header: 'Job desc', accessor: 'JOB_REF', width: 300 },
	{ Header: 'Est. College Hrs', accessor: 'ESTIMATED_LABOR', width: 150 },
	{ Header: 'Est. Material Cost', accessor: 'ESTIMATED_MAT', width: 150 },
	{ Header: 'Status', accessor: 'STATUS_NAME', width: 100 }
];

const estimateTotalColumns = [
	{ Header: 'Estimated Labor', accessor: 'estimate_total.ESTIMATED_LABOR', row: 0, col: 0 },
	{ Header: 'Estimated Matl', accessor: 'estimate_total.ESTIMATED_MAT', row: 0, col: 1 },
	{ Header: 'Total Estimate', accessor: 'estimate_total.TOTAL_ESTIMATE', row: 0, col: 2 }
];

const addJobColumns = [
	{ Header: 'Add job to project', accessor: 'add_job.JOB_ID', type: 'select',
		subscript: 'Totals will update when project is saved',
		options: { value: 'JOB_ID', label: 'JOB_DESC', entity: 'RECHARGES.ADD_JOB_SELECT_VW',
			search: { Attribute: 'PROJECT_COUNT', Operator: '=', LongValue: 0 },
			order: ['CREATE_FIRST_ORDER','JOB_ID desc'] }}
];

const jobColumns = [
	{ Header: '', accessor: 'id', width: 25, 
		Cell: props => <XSquare style={{ cursor: 'pointer' }} onClick={() => props.parent.removeJob(props.row.values.JOB_ID, true)} /> },
	{ Header: 'Job', accessor: 'JOB_ID', width: 70,
		Cell: props => <HashLink onClick={() => props.parent.openJob(props.value, false)}>{props.value}</HashLink> },
	{ Header: 'Job Status', accessor: 'STATUS_NAME' },
	{ Header: 'Job Desc', accessor: 'JOB_REF', width: 300 },
	{ Header: 'Total Mat.', accessor: 'TOTAL_MAT' },
	{ Header: 'Total Labor Hrs', accessor: 'TOTAL_LABOR' },
	{ Header: 'Speedtype', accessor: 'SPEEDTYPE' },
	{ Header: 'Customer', accessor: 'CUSTOMER_NAME' },
	{ Header: 'Job PI', accessor: 'PI_NAME' }
];

const jobTotalColumns = [
	{ Header: 'Total Matl', accessor: 'job_total.TOTAL_MAT', row: 0, col: 0 },
	{ Header: 'Total Labor', accessor: 'job_total.TOTAL_LABOR', row: 0, col: 1 },
	{ Header: 'Total Cost', accessor: 'job_total.TOTAL_COST', row: 0, col: 2 }
];

const scopeColumns = [
	{ Header: 'Scope', accessor: 'project_scope.PROJECT_SCOPE', type: 'editor', row: 0, col: 0 },
	{ Header: 'Comments', accessor: 'project_scope.COMMENTS', type: 'textarea', row: 2, col: 0 }
];

const emptyDoc = {
	id: null,
	DOCU_ID: null,
	PROJECT_ID: null,
	DOC_NAME: '',
	PROJECT_DOC: null,
	PROJECT_FILE: null,
	COMMENTS: null,
	UPDATED: null
};

const docTableColumns = [
	{ Header: '', accessor: 'PROJECT_FILE', show: false },
	{ Header: 'ID', accessor: 'DOCU_ID', width: 70,
		Cell: props => props.parent.props.parent.renderDocDownload(props) },
	{ Header: 'Name', accessor: 'DOC_NAME', width: 125 },
	{ Header: 'Image', accessor: 'PROJECT_DOC', width: 306,
		Cell: props => props.parent.props.parent.renderDocImage(props.value) },
	{ Header: 'Type', accessor: 'TYPE', width: 50 },
	{ Header: 'Description', accessor: 'DOC_DESC', width: 400 },
	{ Header: 'Updated', accessor: 'UPDATED', width: 200 }
];

const docEditColumns = [
	{ Header: 'Name', accessor: 'DOC_NAME', row: 0, col: 0 },
	{ Header: 'New File', accessor: 'PROJECT_FILE', type: 'file', row: 0, col: 1 },
	{ Header: 'Updated', accessor: 'UPDATED', row: 0, col: 2, editable: false },
	{ Header: 'Description', accessor: 'DOC_DESC', type: 'textarea', row: 1, col: 0, colwidth: 12 }
];

const emptyComment = {
	id: null,
	COMMENT_ID: '(new)',
	JOB_ID: null,
	COMMENT_TEXT: '',
	COMMENT_STATUS: null,
	COMMENTS: '',
	CREATED_BY: '',
	DATE_CREATED: null,
	MODIF_BY: '',
	DATE_MODIF: null,
	DELETE_FLAG: null
};

const commentTableColumns = [
	{ Header: 'ID', accessor: 'COMMENT_ID', width: 60 },
	{ Header: 'Comment', accessor: 'COMMENT_TEXT', width: 400 },
	{ Header: 'Entered', accessor: 'ENTERED', width: 200 },
	{ Header: 'Status', accessor: 'COMMENT_STATUS', width: 100,
		Cell: props => <>{props.value === 'Y' ? 'Published' : 'Not Published'}</> },
	{ Header: 'Updated', accessor: 'UPDATED', width: 200 }
];

const commentEditColumns = [
	{ Header: 'Comment', accessor: 'COMMENT_TEXT', type: 'editor', row: 0, col: 0, colwidth: 10 },
	{ Header: '', accessor: 'COMMENT_STATUS', type: 'checkbox', label: 'Publish', row: 0, col: 1, colwidth: 2 }
];

const summaryColumns = [
	{ Header: 'PI', accessor: 'summary.PI_NAME', row: 0, col: 0 },
	{ Header: 'Customer', accessor: 'summary.CUSTOMER_NAME', row: 0, col: 1 },
	{ Header: 'Project Name', accessor: 'summary.PROJECT_NAME', row: 1, col: 0 },
	{ Header: 'Project Status', accessor: 'summary.STATUS_NAME', row: 1, col: 1 },
	{ Header: 'Description', accessor: 'summary.PROJECT_DESC', row: 2, col: 0 },
	{ Header: 'Project Duration', accessor: 'summary.PROJECT_DURATION', row: 2, col: 1, fragment: true },
	{ Header: 'Total Estimated Labor', accessor: 'sum_totals.ESTIMATED_LABOR', row: 3, col: 0 },
	{ Header: 'Total Estimated Material', accessor: 'sum_totals.ESTIMATED_MAT', row: 3, col: 1 },
	{ Header: 'Total Job Labor', accessor: 'sum_totals.TOTAL_LABOR', row: 4, col: 0 },
	{ Header: 'Total Job Material', accessor: 'sum_totals.TOTAL_MAT', row: 4, col: 1 }
];

export default withProjectDefaultRoute(Project);
